import React, { useState } from 'react';
import { RotateCcw, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog.jsx';
import type { DraftDescription } from './draftDescription';

interface Props {
  description: DraftDescription;
  /** Discard the draft, the retained form state and any held images. */
  onStartFresh: () => void;
  /** Keep the restored work, just stop saying so. */
  onDismiss: () => void;
}

/**
 * Tells the user their unfinished study came back, and gives them one click to reject it.
 *
 * A silent restore is defensible for a draft that dies with the tab; this one is in
 * localStorage and survives a reboot, so the user has no way to tell whether the filled
 * form in front of them is theirs from ten minutes ago or from last Tuesday. #376 is
 * exactly the harm of a form that looks correctly filled while holding the wrong study,
 * so the restore is announced rather than assumed.
 *
 * Informational (teal), not a warning (amber): nothing has gone wrong, we are reporting
 * something done on the user's behalf.
 */
const DraftRestoredBanner: React.FC<Props> = ({ description, onStartFresh, onDismiss }) => {
  const [confirming, setConfirming] = useState(false);

  const detail = description.restored.length
    ? `Restored from ${description.age} — ${description.restored.join(', ')}.`
    : `Restored from ${description.age}.`;

  return (
    <>
      <div
        className="mb-4 flex flex-wrap items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 px-3.5 py-3"
        role="status"
        aria-live="polite"
      >
        <span className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full border border-blue-100 bg-white">
          <RotateCcw className="h-[15px] w-[15px] text-blue-700" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-semibold text-blue-900">We brought your study back</span>
          <span className="mt-0.5 block text-[12.5px] text-blue-800">
            {detail}
            {description.imagesNeedReupload && ' Images need re‑uploading.'}
          </span>
        </span>
        {/* Actions wrap to their own row on a narrow viewport rather than squeezing the
            text — /simulate already scrolls sideways at 390px in places (#518). */}
        <span className="flex flex-none items-center gap-1 max-sm:w-full max-sm:justify-end">
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold text-blue-700 hover:bg-white"
          >
            Start fresh
          </button>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="rounded-md px-2 py-1.5 text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </span>
      </div>

      {/* Discarding is unrecoverable, so it asks once — and names what is at stake, since
          the user may have more typed here than they remember. */}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Start fresh?</DialogTitle>
            <DialogDescription>
              This permanently discards the restored study
              {description.restored.length ? ` — ${description.restored.join(', ')}` : ''}.
              It cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-[13.5px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              Keep my work
            </button>
            <button
              type="button"
              onClick={() => { setConfirming(false); onStartFresh(); }}
              className="rounded-lg bg-red-600 px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-red-700"
            >
              Discard and start fresh
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DraftRestoredBanner;
